import { GARDEN_OBJECT_TYPES } from '../constants/gardenObjectTypes';
import { hexToRgba } from '../constants/flowerColors';
import { isBloomingInMonth, getBloomColorHex } from './bloomUtils';

/**
 * bloomMode - Nicht-destruktiver Blüh-Modus über den Fabric-Canvas (#270 / #608)
 *
 * Färbt blühende Pflanzen im gewählten Monat in ihrer Blütenfarbe (Innenkreis)
 * und dimmt nicht blühende Pflanzen. Pflanzen ohne Blühangabe bleiben neutral.
 *
 * **Strikt nicht-destruktiv:** Beim ersten Anfassen einer Pflanze werden ihre
 * Original-Werte (Innenkreis-Füllung + Gruppen-Opacity) in `obj._bloomStash`
 * gesichert. `restoreBloom()` stellt exakt diesen Zustand wieder her und löscht
 * den Stash. Es werden **keine** Commands erzeugt (kein Undo-Eintrag) und der
 * Persistenz-Serializer ignoriert die transienten Felder — Speichern im
 * Blüh-Modus speichert die Originalfarben.
 */

/** Innenkreis-Deckkraft für blühende Pflanzen (satt genug, um zu „leuchten"). */
export const BLOOM_FILL_OPACITY = 0.85;
/** Gesamt-Opacity für nicht blühende Pflanzen (leises Zurücktreten). */
export const DIMMED_OPACITY = 0.55;

const isPlant = (obj) => obj?.objectType === GARDEN_OBJECT_TYPES.PLANT;

/** Original-Zustand einmalig sichern (idempotent). */
function stashOnce(obj) {
  if (obj._bloomStash) return;
  obj._bloomStash = {
    fill: obj._innerCircle ? obj._innerCircle.fill : null,
    opacity: obj.opacity ?? 1,
  };
}

/**
 * Blüh-Darstellung für einen Monat auf alle Pflanzen des Canvas anwenden.
 * Idempotent und live-tauglich: erneuter Aufruf (z. B. Monatswechsel oder neu
 * platzierte Pflanze) rechnet frisch aus den Original-Werten.
 *
 * @param {object} canvas - Fabric-Canvas
 * @param {number} month - 0-indiziert (0 = Januar … 11 = Dezember)
 */
export function applyBloomMonth(canvas, month) {
  if (!canvas || typeof canvas.getObjects !== 'function') return;

  for (const obj of canvas.getObjects()) {
    if (!isPlant(obj)) continue;
    stashOnce(obj);
    const inner = obj._innerCircle;
    const original = obj._bloomStash;
    const blooming = isBloomingInMonth(obj.plantProperties, month);

    if (blooming === true) {
      const hex = getBloomColorHex(obj.plantProperties, obj.selectedColor);
      if (inner) inner.set({ fill: hex ? hexToRgba(hex, BLOOM_FILL_OPACITY) : original.fill });
      obj.set({ opacity: 1 });
    } else if (blooming === false) {
      // Nicht blühend: Originalfüllung behalten, Gesamtdarstellung dimmen.
      if (inner) inner.set({ fill: original.fill });
      obj.set({ opacity: DIMMED_OPACITY });
    } else {
      // Keine Blühangabe: neutral (Originaldarstellung, volle Opacity).
      if (inner) inner.set({ fill: original.fill });
      obj.set({ opacity: 1 });
    }
    obj.dirty = true;
  }

  canvas.requestRenderAll();
}

/**
 * Blüh-Modus verlassen: exakten Originalzustand wiederherstellen und Stash lösen.
 *
 * @param {object} canvas - Fabric-Canvas
 */
export function restoreBloom(canvas) {
  if (!canvas || typeof canvas.getObjects !== 'function') return;

  for (const obj of canvas.getObjects()) {
    if (!obj._bloomStash) continue;
    if (obj._innerCircle) obj._innerCircle.set({ fill: obj._bloomStash.fill });
    obj.set({ opacity: obj._bloomStash.opacity });
    obj.dirty = true;
    delete obj._bloomStash;
  }

  canvas.requestRenderAll();
}
